import { Typography, Container, Box, Paper, Button, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Link, Accordion, AccordionSummary, AccordionDetails, TextField, CircularProgress } from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TimerIcon from '@mui/icons-material/Timer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import stripePromise from '../utils/stripeClient';
import { supabase } from '../utils/supabaseClient';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { useActiveSale } from '../hooks/useActiveSale';
import SuccessStories from './SuccessStories';
import { validateCode } from '../utils/referralUtils';
import CredentialLogos from './CredentialLogos';

const faqItems = [
  {
    question: "How is this different from just using LeetCode?",
    answer: "While LeetCode is a great platform, it has over 3500 problems which can be overwhelming. It took me over 4 years to solve all of them. I have carefully curated most of the 3500+ problems from my thousands of hours of solving problems into 20+ patterns and templates. Our templates and patterns help you understand my thought process and the underlying concepts instead of memorizing solutions."
  },
  {
    question: "What exactly do I get?",
    answer: "You can browse the free content currently on the website. Once you purchase, you get access to the premium chapters -- with over 300+ pages of pure gold templates and patterns, as well as the premium quizzes and flashcards."
  },
  {
    question: "Do I get lifetime access?",
    answer: "Yes! Once you purchase the lifetime plan, you get lifetime access to all current content and future updates. There are no recurring fees or hidden charges."
  },
  {
    question: "How long does it take to complete the course?",
    answer: "Most students complete the course in 1-2 months when studying 1-2 hours per day. However, you can go at your own pace since you have lifetime access."
  },
  {
    question: "What if I'm not satisfied with the course?",
    answer: "We offer a satisfaction guarantee. I'm confident in my product, so if you're not completely satisfied, just email me."
  },
  {
    question: "Is this suitable for beginners?",
    answer: "The course assumes basic programming knowledge in any language. We start with fundamental patterns and gradually progress to more advanced topics. Each concept is explained from the ground up. There are more beginner topics like arrays and hashing, as well as more advanced such as monotonic stacks -- I'm confident that beginners and experienced programmers alike will find value in this course."
  },
  {
    question: "What programming language is used?",
    answer: "All solutions and templates are provided in Python, the most popular languages for coding interviews. The patterns and concepts can be applied to any language."
  },
  {
    question: "Do you offer video solutions?",
    answer: "Currently, we only offer text solutions. We believe this is the most efficient, after all the videos will just be a transcript of the text solutions."
  },
  // {
  //   question: "Do you provide interview preparation beyond coding?",
  //   answer: "Yes! Our course includes tips on system design, behavioral interviews, salary negotiation, and general interview strategies based on real FAANG interview experiences."
  // },
  // {
  //   question: "How do I get help if I'm stuck?",
  //   answer: "Each problem comes with detailed explanations and multiple approaches. You can also email us directly for personalized help, and we typically respond within 24 hours."
  // }
];

// Base prices (before any discount)
const BASE_PRICES = {
  monthly: 149,
  yearly: 299,
  lifetime: 699
};

function PricingDetails({ faqItems }) {
  const included = [
    ['Pattern courses', '20+ interview patterns with templates and tradeoffs.'],
    ['TopSWE100', 'A focused problem set for repeated, measurable practice.'],
    ['Quizzes', 'Short checks that expose gaps before interviews do.'],
    ['Flashcards', 'Active recall for signals, templates, and complexity.'],
  ];

  const comparison = [
    ['Curation', 'Pattern-first path', 'Large problem library'],
    ['Practice loop', 'Course, quiz, flashcard, roadmap', 'Mostly problem solving'],
    ['Updates', 'Included with active access', 'Varies by platform'],
  ];

  return (
    <Box sx={{ mb: 9 }}>
      <Box sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto', mb: 4 }}>
        <Typography variant="overline" sx={{
          display: 'inline-flex',
          px: 2,
          py: 0.6,
          mb: 2,
          borderRadius: 999,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          color: 'text.primary',
          fontWeight: 800,
        }}>
          What is included
        </Typography>
        <Typography variant="h3" sx={{ color: 'text.primary', fontSize: { xs: '2.1rem', md: '3.5rem' }, fontWeight: 850, lineHeight: 1 }}>
          A focused system, not another problem dump.
        </Typography>
        <Typography sx={{ color: 'text.secondary', mt: 1.5, lineHeight: 1.7 }}>
          Everything is organized around recognizing the pattern, drilling it, and returning to it until it is reliable.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2.5, mb: 4 }}>
        {included.map(([title, body], index) => (
          <Paper key={title} elevation={0} sx={{ p: 3 }}>
            <Typography sx={{ color: 'secondary.main', fontWeight: 850, mb: 1 }}>{String(index + 1).padStart(2, '0')}</Typography>
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 800, mb: 1 }}>{title}</Typography>
            <Typography sx={{ color: 'text.secondary', lineHeight: 1.6 }}>{body}</Typography>
          </Paper>
        ))}
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Area</TableCell>
              <TableCell>TopSWE</TableCell>
              <TableCell>Typical alternative</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {comparison.map(([area, topswe, other]) => (
              <TableRow key={area}>
                <TableCell sx={{ color: 'text.primary', fontWeight: 800 }}>{area}</TableCell>
                <TableCell sx={{ color: 'text.primary' }}>{topswe}</TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{other}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '0.8fr 1.2fr' }, gap: 2.5, alignItems: 'start' }}>
        <Paper elevation={0} sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 850, mb: 1 }}>30-day guarantee</Typography>
          <Typography sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
            Try the system, review the materials, and decide whether it fits your prep workflow. The pricing stays simple: monthly, yearly, or lifetime.
          </Typography>
        </Paper>
        <Box>
          {faqItems.slice(0, 6).map((item) => (
            <Accordion key={item.question} disableGutters sx={{ mb: 1.5 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ color: 'text.primary', fontWeight: 750 }}>{item.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{item.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function Purchase() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasActiveSubscription, hasLifetimeAccess } = useSubscriptionStatus();
  const { sale: activeSale } = useActiveSale();

  // Referral code state
  const [referralCode, setReferralCode] = useState('');
  const [referralValidation, setReferralValidation] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);

  // Calculate discounted prices based on active sale
  const discountPercent = activeSale?.percentOff || 0;
  const prices = {
    monthly: Math.round(BASE_PRICES.monthly * (1 - discountPercent / 100)),
    yearly: Math.round(BASE_PRICES.yearly * (1 - discountPercent / 100)),
    lifetime: Math.round(BASE_PRICES.lifetime * (1 - discountPercent / 100))
  };

  // Read ref from URL on mount
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      validateReferralCode(refCode);
    }
  }, [searchParams]);

  const validateReferralCode = async (code) => {
    if (!code || code.length < 4) {
      setReferralValidation(null);
      return;
    }

    setValidatingCode(true);
    try {
      const result = await validateCode(code);
      setReferralValidation(result);
    } catch (error) {
      console.error('Error validating code:', error);
      setReferralValidation({ valid: false });
    } finally {
      setValidatingCode(false);
    }
  };

  const handleReferralCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setReferralCode(code);
    // Debounce validation
    if (code.length >= 4) {
      validateReferralCode(code);
    } else {
      setReferralValidation(null);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubscription = async (priceId, isLifetime = false) => {
    if (!user) {
      navigate('/signin');
      return;
    }

    // If they have lifetime access, redirect to subscription management
    if (hasLifetimeAccess) {
      navigate('/subscription');
      return;
    }

    // If they have an active subscription but are NOT trying to get lifetime access,
    // redirect them to subscription management
    if (hasActiveSubscription && !isLifetime) {
      navigate('/subscription');
      return;
    }

    try {
      //console.log('Starting subscription process...', { priceId, isLifetime, user });
      
      //console.log('Creating checkout session...');

      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          priceId,
          isLifetime,
          userId: user.id,
          customerEmail: user.email,
          referralCode: referralValidation?.valid && referralValidation.type === 'referral'
            ? referralValidation.code : null,
          influencerCode: referralValidation?.valid && referralValidation.type === 'influencer'
            ? referralValidation.code : null
        }
      });

      //console.log('Checkout session response:', { data, error });

      // if (error && error instanceof FunctionsHttpError) {
      //   const errorMessage = await error.context.json()
      //   console.log('Function returned an error', errorMessage)
      // }

      if (error) {
        //console.error('Error creating checkout session:', error);
        return;
      }

      // Get Stripe instance
      //console.log('Getting Stripe instance...');
      const stripe = await stripePromise;

      // Redirect to Stripe Checkout
      //console.log('Redirecting to checkout...');
      const { error: stripeError } = await stripe.redirectToCheckout({ 
        sessionId: data.sessionId 
      });

      if (stripeError) {
        //console.error('Stripe redirect error:', stripeError);
      }

    } catch (error) {
      //console.error('Subscription error:', error);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: (theme) => theme.palette.custom.heroGradient,
      position: 'relative',
      overflow: 'hidden',
      '& .MuiPaper-root, & .MuiAccordion-root, & .MuiTableContainer-root': {
        background: (theme) => `${theme.palette.background.paper} !important`,
        backgroundImage: 'none !important',
        border: (theme) => `1px solid ${theme.palette.divider} !important`,
        boxShadow: (theme) => theme.palette.mode === 'dark'
          ? '0 24px 80px rgba(0,0,0,0.24) !important'
          : '0 24px 80px rgba(82,107,170,0.12) !important',
        backdropFilter: 'blur(18px)',
      },
      '& .MuiButton-contained': {
        background: (theme) => `${theme.palette.primary.main} !important`,
        color: (theme) => `${theme.palette.primary.contrastText} !important`,
      },
      '& .MuiButton-contained:hover': {
        background: (theme) => `${theme.palette.primary.dark} !important`,
      },
      '& .MuiTypography-root': {
        letterSpacing: 0,
      },
    }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 8 }, pb: 4, position: 'relative', zIndex: 1 }}>
        {/* Hero Section */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 8,
          py: { xs: 7, md: 9 },
          px: { xs: 2, md: 4 },
          position: 'relative',
        }}>
          <Typography
            variant="overline"
            sx={{
              display: 'inline-flex',
              px: 2,
              py: 0.6,
              mb: 2,
              borderRadius: 999,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              color: 'text.primary',
              fontWeight: 800,
            }}
          >
            Pricing
          </Typography>
          <Typography 
            variant="h2" 
            sx={{ 
              color: 'text.primary',
              fontWeight: 850,
              mb: 3,
              fontSize: { xs: '2.8rem', md: '5rem' },
              lineHeight: 0.95,
            }}
          >
            Choose the interview system.
          </Typography>
          <Typography 
            sx={{ 
              color: 'text.secondary',
              mb: 6,
              maxWidth: 680,
              mx: 'auto',
              lineHeight: 1.7,
              fontSize: '1.12rem',
            }}
          >
            Get full access to pattern templates, TopSWE100 tracking, quizzes, flashcards, and future course updates.
          </Typography>

          {/* Dynamic Promotion Banner */}
          {activeSale && (
            <Box sx={{
              mb: 6,
              p: 3,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 5,
              maxWidth: 600,
              mx: 'auto',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
                animation: 'shimmer 3s infinite',
              },
              '@keyframes shimmer': {
                '0%': { left: '-100%' },
                '100%': { left: '100%' }
              }
            }}>
              <Typography
                variant="h4"
                sx={{
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  fontWeight: 700,
                  mb: 2
                }}
              >
                <LocalOfferIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
                {activeSale.name}
                <TimerIcon sx={{ fontSize: 32 }} />
              </Typography>
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {activeSale.percentOff}% OFF - {activeSale.description}
              </Typography>
              {activeSale.promoCode && (
                <Typography variant="h6" sx={{ color: 'secondary.main', mt: 2, fontWeight: 700 }}>
                  Use code: <span style={{ background: '#1a1a2e', padding: '4px 12px', borderRadius: '4px', fontFamily: 'monospace' }}>{activeSale.promoCode}</span>
                </Typography>
              )}
              {activeSale.timeRemaining && (
                <Typography variant="body1" sx={{ color: 'text.secondary', mt: 1, fontSize: '0.9rem' }}>
                  * Ends in {activeSale.timeRemaining.days > 0
                    ? `${activeSale.timeRemaining.days} days`
                    : `${activeSale.timeRemaining.hours} hours`}. Secure your spot now!
                </Typography>
              )}
            </Box>
          )}

        {/* Company Logos Section */}
        <Box sx={{ 
          mb: 8,
        }}>
          <Typography 
            variant="overline" 
            sx={{ 
              color: 'text.primary', 
              mb: 4,
              letterSpacing: 1,
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '1rem',
              position: 'relative',
            }}
          >
            Created by a team who worked at
          </Typography>
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: 2,
              flexWrap: 'wrap',
              px: { xs: 2, md: 4 },
              mx: 'auto',
              maxWidth: 1000,
              py: 3,
              bgcolor: 'background.paper',
              borderRadius: 5,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
          <CredentialLogos compact />
        </Box>
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'text.secondary', 
              mt: 6,
              fontStyle: 'italic',
              fontSize: '1.1rem'
            }}
          >
            Join thousands of successful engineers who transformed their careers
          </Typography>
        </Box>
        </Box>

        {/* Pricing Section */}
        <Box sx={{ 
          mb: 8,
          py: { xs: 6, md: 8 },
          px: { xs: 0, md: 2 },
        }}>
          <Typography 
            variant="h3" 
            sx={{ 
              textAlign: 'center',
              mb: 3,
              color: 'text.primary',
              fontWeight: 850,
            }}
          >
            Choose Your Plan
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              mb: 4,
              color: 'text.secondary',
              maxWidth: 600,
              mx: 'auto'
            }}
          >
            Start your journey to FAANG with our proven system. All plans include full access to courses, quizzes, and flashcards.
          </Typography>

          {/* Referral Code Input */}
          <Box sx={{
            maxWidth: 400,
            mx: 'auto',
            mb: 6,
            px: 2
          }}>
            <TextField
              fullWidth
              label="Referral or Promo Code (Optional)"
              value={referralCode}
              onChange={handleReferralCodeChange}
              placeholder="Enter code here"
              InputProps={{
                endAdornment: validatingCode ? (
                  <CircularProgress size={20} sx={{ color: '#5168ff' }} />
                ) : referralValidation?.valid ? (
                  <CheckCircleIcon sx={{ color: '#15905f' }} />
                ) : referralCode && referralCode.length >= 4 && !referralValidation?.valid ? (
                  <ErrorIcon sx={{ color: '#c24150' }} />
                ) : null,
                sx: {
                  color: 'text.primary',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: referralValidation?.valid
                      ? '#15905f'
                      : referralCode && referralCode.length >= 4 && !referralValidation?.valid
                        ? '#c24150'
                        : 'rgba(81, 104, 255, 0.3)'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: referralValidation?.valid ? '#15905f' : '#5168ff'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: referralValidation?.valid ? '#15905f' : '#5168ff'
                  }
                }
              }}
              InputLabelProps={{
                sx: { color: 'text.secondary' }
              }}
              helperText={
                referralValidation?.valid
                  ? referralValidation.type === 'influencer'
                    ? `Using ${referralValidation.influencerName}'s code`
                    : 'Valid referral code applied!'
                  : referralCode && referralCode.length >= 4 && !referralValidation?.valid
                    ? 'Invalid code'
                    : 'Have a referral code? Enter it here for credit.'
              }
              FormHelperTextProps={{
                sx: {
                  color: referralValidation?.valid
                    ? '#15905f'
                    : referralCode && referralCode.length >= 4 && !referralValidation?.valid
                      ? '#c24150'
                      : 'text.secondary'
                }
              }}
              sx={{
                '& .MuiInputBase-input': {
                  textTransform: 'uppercase'
                }
              }}
            />
          </Box>

          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: { xs: 3, md: 4 },
            flexWrap: 'wrap',
            px: { xs: 0, md: 2 }
          }}>
            {/* Monthly Plan */}
            <Paper sx={{ 
              p: 5,
              width: { xs: '100%', sm: 340 },
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              transition: 'transform 180ms ease, border-color 180ms ease',
              '&:hover': {
                transform: 'translateY(-6px)',
                borderColor: 'secondary.main',
              }
            }}>
          <Typography 
            variant="h4" 
            sx={{ 
              color: 'text.primary',
              mb: 2,
              fontWeight: 700,
              textAlign: 'center'
            }}
          >
            Monthly
          </Typography>
          
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            {discountPercent > 0 && (
              <Typography variant="body1" sx={{ color: 'text.secondary', textDecoration: 'line-through', mb: 1 }}>
                Regular ${BASE_PRICES.monthly}/month
              </Typography>
            )}
            <Typography
              variant="h2"
              sx={{
                color: 'text.primary',
                fontWeight: 800,
                mb: 1,
                position: 'relative'
              }}
            >
              ${prices.monthly}
              <Typography
                variant="h6"
                component="span"
                sx={{ color: 'text.secondary', ml: 1, fontWeight: 400 }}
              >
                /month
              </Typography>
            </Typography>
            {discountPercent > 0 && (
              <Box
                sx={{
                  display: 'inline-block',
                  bgcolor: 'rgba(255, 107, 53, 0.2)',
                  color: '#c24150',
                  px: 2,
                  py: 0.5,
                  borderRadius: 3,
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  mb: 3
                }}
              >
                🔥 {discountPercent}% OFF {activeSale?.name}
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 4 }}>
            {[
              'Access to all premium patterns',
              'Interactive quizzes & flashcards', 
              'TopSWE100 problem set',
              'Regular content updates'
            ].map((feature, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography sx={{ color: '#15905f' }}>✓</Typography>
                <Typography sx={{ color: 'text.primary' }}>{feature}</Typography>
              </Box>
            ))}
          </Box>

          <Button 
            fullWidth 
            variant="outlined" 
            size="large"
            sx={{ 
              py: 2,
              borderRadius: 999,
              borderColor: 'primary.main',
              color: 'text.primary',
              fontWeight: 700,
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover'
              }
            }}
            onClick={() => handleSubscription(process.env.REACT_APP_STRIPE_PRICE_MONTHLY, false)}
            disabled={hasLifetimeAccess}
          >
            {user ? (
              hasLifetimeAccess ? 'You have lifetime access' : 
              hasActiveSubscription ? 'Manage Subscription' : 'Start Monthly Plan'
            ) : (
              'Sign in to Subscribe'
            )}
          </Button>
        </Paper>

        {/* Lifetime Plan - HIGHLIGHTED */}
        <Paper sx={{ 
          p: 4, 
          width: { xs: '100%', sm: 340 },
          bgcolor: 'rgba(255,255,255,0.86)',
          border: '1px solid',
          borderColor: 'secondary.main',
          position: 'relative',
          overflow: 'hidden',
          transform: { xs: 'none', md: 'scale(1.05)' },
          zIndex: 2,
          boxShadow: '0 28px 90px rgba(82, 107, 170, 0.24)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: { xs: 'translateY(-8px)', md: 'scale(1.05) translateY(-8px)' },
            boxShadow: '0 32px 100px rgba(82, 107, 170, 0.28)',
          }
        }}>
          {/* Popular Badge */}
          <Box sx={{
            position: 'absolute',
            top: -1,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'primary.main',
            color: 'primary.contrastText',
            px: 4,
            py: 1,
            borderRadius: '0 0 20px 20px',
            fontSize: '0.9rem',
            fontWeight: 700,
            zIndex: 3
          }}>
            ⭐ MOST POPULAR
          </Box>

          <Typography 
            variant="h4" 
            sx={{ 
              color: 'text.primary',
              mb: 2,
              fontWeight: 700,
              textAlign: 'center',
              mt: 2
            }}
          >
            Lifetime Access
          </Typography>
          
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            {discountPercent > 0 && (
              <Typography variant="body1" sx={{ color: 'text.secondary', textDecoration: 'line-through', mb: 1 }}>
                Regular ${BASE_PRICES.lifetime} one-time
              </Typography>
            )}
            <Typography
              variant="h1"
              sx={{
                color: 'text.primary',
                fontWeight: 800,
                mb: 1,
                fontSize: '3rem'
              }}
            >
              ${prices.lifetime}
              <Typography
                variant="h6"
                component="span"
                sx={{ color: 'text.secondary', ml: 1, fontWeight: 400 }}
              >
                one-time
              </Typography>
            </Typography>

            {discountPercent > 0 && (
              <Box
                sx={{
                  display: 'inline-block',
                  bgcolor: 'rgba(255, 107, 53, 0.2)',
                  color: '#c24150',
                  px: 2,
                  py: 0.5,
                  borderRadius: 3,
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  mb: 2
                }}
              >
                🔥 {discountPercent}% OFF {activeSale?.name}
              </Box>
            )}

            <Typography variant="body1" sx={{ color: '#15905f', fontWeight: 600 }}>
              💰 Save $100s vs monthly plan!
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            {[
              'Everything in Monthly plan',
              'Lifetime access - no recurring fees',
              'All future content updates included',
              'Priority email support',
              'Exclusive lifetime member perks'
            ].map((feature, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography sx={{ color: '#15905f' }}>✓</Typography>
                <Typography sx={{ color: 'text.primary', fontWeight: index < 2 ? 600 : 400 }}>{feature}</Typography>
              </Box>
            ))}
          </Box>

          <Button 
            fullWidth 
            variant="contained" 
            size="large"
            sx={{ 
              py: 2.5,
              borderRadius: 3,
              background: 'primary.main',
              fontWeight: 700,
              fontSize: '1.1rem',
              boxShadow: '0 8px 20px rgba(81, 104, 255, 0.4)',
              '&:hover': {
                background: 'primary.dark',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 25px rgba(81, 104, 255, 0.5)',
              }
            }}
            onClick={() => handleSubscription(process.env.REACT_APP_STRIPE_PRICE_LIFETIME, true)}
            disabled={hasLifetimeAccess}
          >
            {user ? (
              hasLifetimeAccess ? '✅ You have lifetime access' : 
              hasActiveSubscription ? 'Upgrade to Lifetime' : '🚀 Get Lifetime Access'
            ) : (
              'Sign in to Subscribe'
            )}
          </Button>
        </Paper>

            {/* Yearly Plan */}
            <Paper sx={{ 
              p: 5, 
              width: { xs: '100%', sm: 340 },
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'transform 180ms ease, border-color 180ms ease',
              '&:hover': {
                transform: 'translateY(-6px)',
                borderColor: 'secondary.main',
              }
            }}>
          <Typography 
            variant="h4" 
            sx={{ 
              color: 'text.primary',
              mb: 2,
              fontWeight: 700,
              textAlign: 'center'
            }}
          >
            Yearly
          </Typography>
          
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            {discountPercent > 0 && (
              <Typography variant="body1" sx={{ color: 'text.secondary', textDecoration: 'line-through', mb: 1 }}>
                Regular ${BASE_PRICES.yearly}/year
              </Typography>
            )}
            <Typography
              variant="h2"
              sx={{
                color: 'text.primary',
                fontWeight: 800,
                mb: 1
              }}
            >
              ${prices.yearly}
              <Typography
                variant="h6"
                component="span"
                sx={{ color: 'text.secondary', ml: 1, fontWeight: 400 }}
              >
                /year
              </Typography>
            </Typography>
            {discountPercent > 0 && (
              <Box
                sx={{
                  display: 'inline-block',
                  bgcolor: 'rgba(255, 107, 53, 0.2)',
                  color: '#c24150',
                  px: 2,
                  py: 0.5,
                  borderRadius: 3,
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  mb: 2
                }}
              >
                🔥 {discountPercent}% OFF {activeSale?.name}
              </Box>
            )}
            <Typography variant="body2" sx={{ color: 'secondary.main' }}>
              Save vs monthly
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            {[
              'Everything in Monthly plan',
              'Annual billing - save money',
              'Priority support',
              'Early access to new features'
            ].map((feature, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography sx={{ color: '#15905f' }}>✓</Typography>
                <Typography sx={{ color: 'text.primary' }}>{feature}</Typography>
              </Box>
            ))}
          </Box>
          <Button 
            fullWidth 
            variant="outlined" 
            sx={{ mt: 2 }}
            onClick={() => handleSubscription(process.env.REACT_APP_STRIPE_PRICE_YEARLY, false)}
            disabled={hasLifetimeAccess}
          >
            {user ? (
              hasLifetimeAccess ? 'You have lifetime access' : 
              hasActiveSubscription ? 'Manage Subscription' : 'Subscribe Yearly'
            ) : (
              'Sign in to Subscribe'
            )}
          </Button>
            </Paper>
          </Box>
        </Box>

        <PricingDetails faqItems={faqItems} />

        <Box sx={{ display: 'none' }}>
        {/* Learn from FANG Engineer Section */}
        <Box sx={{ 
          mb: 8,
          py: 8,
          px: 4,
          background: 'linear-gradient(135deg, rgba(142, 160, 255, 0.08) 0%, rgba(81, 104, 255, 0.08) 100%)',
          borderRadius: 6,
          border: '1px solid rgba(142, 160, 255, 0.2)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #8ea0ff, #5168ff, #c24150)',
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 6 }}>
            <Box sx={{ 
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8ea0ff, #5168ff)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 4,
              boxShadow: '0 8px 32px rgba(142, 160, 255, 0.3)',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.05)' },
                '100%': { transform: 'scale(1)' }
              }
            }}>
              <Typography variant="h3" sx={{ color: 'text.primary', fontWeight: 800 }}>👑</Typography>
            </Box>
            <Box>
              <Typography 
                variant="h3" 
                sx={{ 
                  background: '#05070c',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 800,
                  mb: 1
                }}
              >
                Learn from a FANG Senior Engineer
              </Typography>
              <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                World-class expertise meets proven results
              </Typography>
            </Box>
          </Box>
      
          <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, rgba(142, 160, 255, 0.1) 0%, rgba(81, 104, 255, 0.1) 100%)',
                border: '2px solid rgba(142, 160, 255, 0.3)',
                borderRadius: 5,
                p: 6,
                position: 'relative',
                boxShadow: '0 12px 40px rgba(142, 160, 255, 0.2)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -2,
                  left: -2,
                  right: -2,
                  bottom: -2,
                  background: 'linear-gradient(45deg, #8ea0ff, #5168ff, #c24150)',
                  borderRadius: 5,
                  zIndex: -1,
                  opacity: 0.7
                }
              }}
            >
              <Typography 
                variant="h4" 
                sx={{ 
                  mb: 6,
                  textAlign: 'center',
                  background: 'linear-gradient(45deg, #c24150, #b7791f)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 700
                }}
              >
                🏆 Elite Credentials & Achievements
              </Typography>
              
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 4
              }}>
                {[
                  {
                    icon: "🌟",
                    title: "LeetCode World Champion",
                    text: "Ranked #1 Worldwide on LeetCode (Peak) out of 5M+ users",
                    link: "/problems",
                    linkText: "View TopSWE100",
                    highlight: true
                  },
                  {
                    icon: "🏢",
                    title: "FANG Senior Engineer",
                    text: "Top 1% Senior Engineer at FANG company",
                    highlight: true
                  },
                  {
                    icon: "🎓",
                    title: "Academic Excellence",
                    text: "Graduated #1 in Computer Science from Top University with 4.0 GPA"
                  },
                  {
                    icon: "💯",
                    title: "Complete Mastery",
                    text: "Solved all 3500+ LeetCode problems & Top 1% in Contests"
                  },
                  {
                    icon: "📺",
                    title: "Content Creator",
                    text: "Created hundreds of educational videos on YouTube and TikTok"
                  },
                  {
                    icon: "👨‍🏫",
                    title: "Industry Mentor",
                    text: "Interviewed, taught, and mentored engineers at FANG companies"
                  }
                ].map((item, index) => (
                  <Box 
                    key={index}
                    sx={{ 
                      p: 3,
                      background: item.highlight 
                        ? 'linear-gradient(135deg, rgba(255, 107, 53, 0.15) 0%, rgba(247, 147, 30, 0.15) 100%)'
                        : 'rgba(255, 255, 255, 0.03)',
                      border: item.highlight 
                        ? '2px solid rgba(255, 107, 53, 0.3)'
                        : '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 4,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: item.highlight 
                          ? '0 8px 32px rgba(255, 107, 53, 0.2)'
                          : '0 8px 32px rgba(81, 104, 255, 0.1)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Typography sx={{ fontSize: '2rem', lineHeight: 1 }}>{item.icon}</Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: item.highlight ? '#c24150' : '#5168ff',
                            fontWeight: 600,
                            mb: 1
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                          {item.text}
                          {item.link && (
                            <>
                              {' '}
                              <Link 
                                href={item.link}
                                target="_blank" 
                                rel="noopener noreferrer"
                                sx={{ 
                                  color: '#c24150',
                                  textDecoration: 'none',
                                  fontWeight: 600,
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                ({item.linkText})
                              </Link>
                            </>
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Instructor Comparison Table */}
        <Box sx={{ mb: 8 }}>
          <Typography 
            variant="h3" 
            sx={{ 
              textAlign: 'center',
              mb: 3,
              background: '#0a0d14',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800,
            }}
          >
            🥊 How We Compare
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              textAlign: 'center',
              mb: 6,
              color: 'text.secondary',
              maxWidth: 700,
              mx: 'auto'
            }}
          >
            See why TopSWE stands out from other coding interview platforms
          </Typography>

          <Box sx={{
            background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.05) 0%, rgba(142, 160, 255, 0.05) 100%)',
            borderRadius: 6,
            p: 4,
            border: '1px solid rgba(81, 104, 255, 0.2)',
            overflow: 'hidden'
          }}>
            <TableContainer 
              component={Box} 
              sx={{ 
                '& .MuiTableCell-root': {
                  borderColor: 'rgba(81, 104, 255, 0.1)',
                  py: 2,
                  px: 3
                },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        color: 'text.primary',
                        background: 'linear-gradient(45deg, rgba(81, 104, 255, 0.2), rgba(142, 160, 255, 0.2))',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        borderTopLeftRadius: 2,
                      }}
                    >
                      📊 Instructor Credentials
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        color: '#5168ff',
                        background: 'linear-gradient(45deg, rgba(81, 104, 255, 0.3), rgba(142, 160, 255, 0.2))',
                        fontWeight: 800,
                        fontSize: '1.1rem',
                        textAlign: 'center'
                      }}
                    >
                      🏆 TopSWE
                    </TableCell>
                    <TableCell sx={{ 
                      color: '#999',
                      background: 'linear-gradient(45deg, rgba(81, 104, 255, 0.1), rgba(142, 160, 255, 0.1))',
                      fontWeight: 600,
                      textAlign: 'center'
                    }}>
                      LeetCode
                    </TableCell>
                    <TableCell sx={{ 
                      color: '#999',
                      background: 'linear-gradient(45deg, rgba(81, 104, 255, 0.1), rgba(142, 160, 255, 0.1))',
                      fontWeight: 600,
                      textAlign: 'center'
                    }}>
                      NeetCode
                    </TableCell>
                    <TableCell sx={{ 
                      color: '#999',
                      background: 'linear-gradient(45deg, rgba(81, 104, 255, 0.1), rgba(142, 160, 255, 0.1))',
                      fontWeight: 600,
                      textAlign: 'center',
                      borderTopRightRadius: 2,
                    }}>
                      AlgoExpert
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={{ 
                    '&:hover': { 
                      backgroundColor: 'rgba(81, 104, 255, 0.08)',
                      transition: 'background-color 0.3s'
                    }
                  }}>
                    <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>🎯 LeetCode Ranking</TableCell>
                    <TableCell sx={{ 
                      color: '#15905f', 
                      fontWeight: 700,
                      textAlign: 'center',
                      background: 'rgba(76, 175, 80, 0.1)',
                    }}>
                      ✨ Rank #1 Worldwide
                    </TableCell>
                    <TableCell sx={{ color: '#c24150', textAlign: 'center' }}>❌ N/A</TableCell>
                    <TableCell sx={{ color: '#b7791f', textAlign: 'center' }}>⚠️ Top 100</TableCell>
                    <TableCell sx={{ color: '#c24150', textAlign: 'center' }}>❌ Not Disclosed</TableCell>
                  </TableRow>
                  {[
                    { metric: "💯 Problems Solved", topswe: "✅ All 3500+ Problems", leetcode: "❌ N/A", neetcode: "✅ All Problems", algo: "⚠️ Not All Problems" },
                    { metric: "🏢 Work Experience", topswe: "✅ Senior @ FANG", leetcode: "❌ Various", neetcode: "✅ L4 @ Google", algo: "✅ L4 @ Google" },
                    { metric: "🎓 Education", topswe: "✅ #1 CS Graduate, 4.0 GPA", leetcode: "❌ Various", neetcode: "✅ UW", algo: "✅ UPenn" },
                    { metric: "📺 Teaching Experience", topswe: "✅ 40k+ Students", leetcode: "❌ Community Only", neetcode: "✅ 500k+ YouTube", algo: "✅ 100k+ Students" },
                    { metric: "🎤 Interview Experience", topswe: "✅ Interviewed FANG Engineers", leetcode: "❌ No Direct Experience", neetcode: "⚠️ Limited", algo: "⚠️ Limited" }
                  ].map((row, index) => (
                    <TableRow key={index} sx={{ 
                      '&:hover': { 
                        backgroundColor: 'rgba(81, 104, 255, 0.08)',
                        transition: 'background-color 0.3s'
                      }
                    }}>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>{row.metric}</TableCell>
                      <TableCell sx={{ 
                        color: '#15905f', 
                        fontWeight: 600,
                        textAlign: 'center',
                        background: 'rgba(76, 175, 80, 0.1)',
                      }}>{row.topswe}</TableCell>
                      <TableCell sx={{ color: row.leetcode.includes('❌') ? '#c24150' : '#b7791f', textAlign: 'center' }}>{row.leetcode}</TableCell>
                      <TableCell sx={{ color: row.neetcode.includes('❌') ? '#c24150' : row.neetcode.includes('⚠️') ? '#b7791f' : '#15905f', textAlign: 'center' }}>{row.neetcode}</TableCell>
                      <TableCell sx={{ color: row.algo.includes('❌') ? '#c24150' : row.algo.includes('⚠️') ? '#b7791f' : '#15905f', textAlign: 'center' }}>{row.algo}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        {/* Living Course Section */}
        <Box sx={{ 
          mb: 8,
          py: 8,
          px: 4,
          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(139, 195, 74, 0.08) 100%)',
          borderRadius: 6,
          border: '2px solid rgba(76, 175, 80, 0.3)',
          textAlign: 'center',
          maxWidth: '900px',
          mx: 'auto',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #15905f, #8bc34a, #15905f)',
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
            <Box sx={{ 
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #15905f, #8bc34a)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 3,
              animation: 'grow 2s infinite',
              '@keyframes grow': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.1)' },
                '100%': { transform: 'scale(1)' }
              }
            }}>
              <Typography variant="h4" sx={{ color: 'text.primary' }}>🌱</Typography>
            </Box>
            <Typography 
              variant="h3" 
              sx={{ 
                background: 'linear-gradient(45deg, #15905f, #8bc34a)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 800
              }}
            >
              A Living, Growing Course
            </Typography>
          </Box>
          
          <Typography variant="h5" sx={{ color: 'text.secondary', mb: 6, fontWeight: 400 }}>
            This isn't just a static course - it's constantly evolving with the industry
          </Typography>
          
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 4,
            mb: 6
          }}>
            {[
              { icon: "📚", title: "New Content", desc: "Fresh topics and chapters added regularly" },
              { icon: "🏆", title: "Contest Updates", desc: "Latest LeetCode contest solutions included" },
              { icon: "🔄", title: "Pattern Evolution", desc: "Updated with newest interview patterns" },
              { icon: "♾️", title: "Lifetime Access", desc: "All future content at no additional cost" }
            ].map((item, index) => (
              <Box key={index} sx={{ 
                p: 3,
                background: 'rgba(76, 175, 80, 0.1)',
                borderRadius: 3,
                border: '1px solid rgba(76, 175, 80, 0.3)',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)' }
              }}>
                <Typography sx={{ fontSize: '2rem', mb: 1 }}>{item.icon}</Typography>
                <Typography variant="h6" sx={{ color: '#15905f', fontWeight: 600, mb: 1 }}>
                  {item.title}
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>{item.desc}</Typography>
              </Box>
            ))}
          </Box>
          
          <Box sx={{ 
            p: 4,
            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(139, 195, 74, 0.2) 100%)',
            borderRadius: 4,
            border: '2px solid rgba(76, 175, 80, 0.5)',
            display: 'inline-block',
            position: 'relative'
          }}>
            <Typography variant="h5" sx={{ 
              color: '#15905f',
              fontWeight: 700,
              mb: 1
            }}>
              💎 "Buy once, learn forever"
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              All future updates included with your lifetime access
            </Typography>
          </Box>
        </Box>

        {/* Changelog Section */}
        <Box sx={{ 
          mb: 8,
          py: 8,
          px: 4,
          background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.08) 0%, rgba(233, 30, 99, 0.08) 100%)',
          borderRadius: 6,
          border: '2px solid rgba(156, 39, 176, 0.3)',
          textAlign: 'center',
          maxWidth: '900px',
          mx: 'auto',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #9c27b0, #e91e63, #9c27b0)',
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
            <Box sx={{ 
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #9c27b0, #e91e63)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 3,
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(156, 39, 176, 0.7)' },
                '70%': { transform: 'scale(1.05)', boxShadow: '0 0 0 10px rgba(156, 39, 176, 0)' },
                '100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(156, 39, 176, 0)' }
              }
            }}>
              <Typography variant="h4" sx={{ color: 'text.primary' }}>📋</Typography>
            </Box>
            <Typography 
              variant="h3" 
              sx={{ 
                background: 'linear-gradient(45deg, #9c27b0, #e91e63)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 800
              }}
            >
              Latest Updates
            </Typography>
          </Box>
          
          <Typography variant="h5" sx={{ color: 'text.secondary', mb: 6, fontWeight: 400 }}>
            See what's new and what we're constantly adding to the course
          </Typography>
          
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            mb: 6,
            textAlign: 'left'
          }}>
            {[
              { 
                date: "July 2025", 
                type: "Design Revamp", 
                title: "Complete UI Modernization", 
                desc: "Major design overhaul with modern animations, glassmorphism effects, enhanced user experience, and streamlined account management",
                badge: "✨"
              },
              { 
                date: "June 2025", 
                type: "Platform Update", 
                title: "Interactive Roadmap Tree", 
                desc: "Launched visual learning path with progress tracking and skill assessments",
                badge: "🎯"
              },
              { 
                date: "May 2025", 
                type: "New Feature", 
                title: "Spaced Repetition Flashcards", 
                desc: "Intelligent flashcard system to help retain key concepts and patterns",
                badge: "🧠"
              },
            ].map((update, index) => (
              <Box key={index} sx={{ 
                p: 4,
                background: 'rgba(156, 39, 176, 0.1)',
                borderRadius: 3,
                border: '1px solid rgba(156, 39, 176, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(156, 39, 176, 0.2)'
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ fontSize: '1.5rem' }}>{update.badge}</Typography>
                    <Box>
                      <Typography variant="h6" sx={{ color: '#e91e63', fontWeight: 600, mb: 0.5 }}>
                        {update.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ 
                          color: '#9c27b0', 
                          fontWeight: 500,
                          bgcolor: 'rgba(156, 39, 176, 0.2)',
                          px: 2,
                          py: 0.5,
                          borderRadius: 2
                        }}>
                          {update.type}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {update.date}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Typography sx={{ color: 'text.secondary', lineHeight: 1.6 }}>{update.desc}</Typography>
              </Box>
            ))}
          </Box>
          
          <Box sx={{ 
            p: 4,
            background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.2) 0%, rgba(233, 30, 99, 0.2) 100%)',
            borderRadius: 4,
            border: '2px solid rgba(156, 39, 176, 0.5)',
            display: 'inline-block',
            position: 'relative'
          }}>
            <Typography variant="h5" sx={{ 
              color: '#e91e63',
              fontWeight: 700,
              mb: 1
            }}>
              🚀 "Always Fresh, Always Growing"
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              New updates added monthly - your investment keeps getting better
            </Typography>
          </Box>
        </Box>

      {/* Feature Comparison Table */}
      <Box sx={{ 
        my: 8,
        py: 8,
        px: 4,
        background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.05) 0%, rgba(142, 160, 255, 0.05) 100%)',
        borderRadius: 6,
        border: '2px solid rgba(81, 104, 255, 0.2)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #5168ff, #8ea0ff, #5168ff)',
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 6 }}>
          <Box sx={{ 
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #5168ff, #8ea0ff)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 3,
            animation: 'comparison-bounce 2s infinite',
            '@keyframes comparison-bounce': {
              '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0)' },
              '40%': { transform: 'translateY(-10px)' },
              '60%': { transform: 'translateY(-5px)' }
            }
          }}>
            <Typography variant="h4" sx={{ color: 'text.primary' }}>📊</Typography>
          </Box>
          <Typography 
            variant="h3" 
            sx={{ 
              background: '#0a0d14',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800,
              textAlign: 'center'
            }}
          >
            Platform Comparison
          </Typography>
        </Box>

        <Typography 
          variant="h6" 
          sx={{ 
            textAlign: 'center',
            mb: 6,
            color: 'text.secondary',
            maxWidth: 700,
            mx: 'auto',
            fontWeight: 400
          }}
        >
          See how TopSWE compares to other popular coding interview platforms
        </Typography>

        <TableContainer 
          component={Box} 
          sx={{ 
            bgcolor: 'transparent',
            borderRadius: 4,
            overflow: 'hidden',
            border: '2px solid rgba(81, 104, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            '& .MuiTableCell-root': {
              borderColor: 'rgba(81, 104, 255, 0.1)',
              py: 2.5,
              px: 3,
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.15) 0%, rgba(142, 160, 255, 0.15) 100%)',
                    color: 'text.primary',
                    fontWeight: 800,
                    fontSize: '1.1rem',
                    width: '25%',
                    borderRight: '2px solid rgba(81, 104, 255, 0.2)'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">🏆</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Features</Typography>
                  </Box>
                </TableCell>
                <TableCell 
                  sx={{ 
                    background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.25) 0%, rgba(142, 160, 255, 0.25) 100%)',
                    color: '#5168ff',
                    fontWeight: 800,
                    fontSize: '1.1rem',
                    width: '25%',
                    textAlign: 'center',
                    borderRight: '2px solid rgba(81, 104, 255, 0.2)',
                    position: 'relative',
                    '&::before': {
                      content: '"👑"',
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      fontSize: '1.2rem'
                    }
                  }}
                >
                  TopSWE 100
                </TableCell>
                <TableCell sx={{ 
                  background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.1) 0%, rgba(142, 160, 255, 0.1) 100%)',
                  color: '#999',
                  fontWeight: 700,
                  fontSize: '1rem',
                  width: '25%',
                  textAlign: 'center',
                  borderRight: '2px solid rgba(81, 104, 255, 0.2)'
                }}>
                  LeetCode
                </TableCell>
                <TableCell sx={{ 
                  background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.1) 0%, rgba(142, 160, 255, 0.1) 100%)',
                  color: '#999',
                  fontWeight: 700,
                  fontSize: '1rem',
                  width: '25%',
                  textAlign: 'center'
                }}>
                  NeetCode
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { 
                  feature: { icon: "💰", text: "Monthly Price" }, 
                  topswe: { text: "$29/month", color: "#15905f", bg: "rgba(76, 175, 80, 0.15)" }, 
                  leetcode: { text: "$35/month", color: "#c24150", bg: "rgba(244, 67, 54, 0.1)" }, 
                  neetcode: { text: "N/A", color: "#c24150", bg: "rgba(244, 67, 54, 0.1)" } 
                },
                { 
                  feature: { icon: "📅", text: "Annual Price" }, 
                  topswe: { text: "$89/year", color: "#15905f", bg: "rgba(76, 175, 80, 0.15)" }, 
                  leetcode: { text: "$159/year", color: "#c24150", bg: "rgba(244, 67, 54, 0.1)" }, 
                  neetcode: { text: "$119/year", color: "#b7791f", bg: "rgba(255, 152, 0, 0.1)" } 
                },
                { 
                  feature: { icon: "♾️", text: "Lifetime Access" }, 
                  topswe: { text: "$249 one-time", color: "#15905f", bg: "rgba(76, 175, 80, 0.15)" }, 
                  leetcode: { text: "Not Available", color: "#c24150", bg: "rgba(244, 67, 54, 0.1)" }, 
                  neetcode: { text: "$299 one-time", color: "#b7791f", bg: "rgba(255, 152, 0, 0.1)" } 
                },
                { 
                  feature: { icon: "📚", text: "Curated Problems" }, 
                  topswe: { text: "100 Essential", color: "#15905f", bg: "rgba(76, 175, 80, 0.15)" }, 
                  leetcode: { text: "3500+ Overwhelming", color: "#c24150", bg: "rgba(244, 67, 54, 0.1)" }, 
                  neetcode: { text: "150 Problems", color: "#b7791f", bg: "rgba(255, 152, 0, 0.1)" } 
                },
                { 
                  feature: { icon: "💼", text: "Interview Insights" }, 
                  topswe: { text: "Real FAANG Experience", color: "#15905f", bg: "rgba(76, 175, 80, 0.15)" }, 
                  leetcode: { text: "Limited", color: "#c24150", bg: "rgba(244, 67, 54, 0.1)" }, 
                  neetcode: { text: "Limited", color: "#b7791f", bg: "rgba(255, 152, 0, 0.1)" } 
                },
                { 
                  feature: { icon: "⏱️", text: "Time to Prepare" }, 
                  topswe: { text: "1-2 months", color: "#15905f", bg: "rgba(76, 175, 80, 0.15)" }, 
                  leetcode: { text: "6+ months", color: "#c24150", bg: "rgba(244, 67, 54, 0.1)" }, 
                  neetcode: { text: "4-6 months", color: "#b7791f", bg: "rgba(255, 152, 0, 0.1)" } 
                }
              ].map((row, index) => (
                <TableRow 
                  key={index}
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: 'rgba(81, 104, 255, 0.08)',
                      transition: 'background-color 0.3s'
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: 'rgba(81, 104, 255, 0.02)'
                    }
                  }}
                >
                  <TableCell 
                    sx={{ 
                      color: 'text.primary', 
                      fontWeight: 700,
                      borderRight: '2px solid rgba(81, 104, 255, 0.1)',
                      background: 'rgba(81, 104, 255, 0.05)'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ fontSize: '1.3rem' }}>{row.feature.icon}</Typography>
                      <Typography sx={{ fontWeight: 700 }}>{row.feature.text}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      textAlign: 'center',
                      borderRight: '2px solid rgba(81, 104, 255, 0.1)',
                      background: row.topswe.bg,
                      position: 'relative'
                    }}
                  >
                    <Box sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      background: row.topswe.bg,
                      border: `1px solid ${row.topswe.color}50`
                    }}>
                      <Typography sx={{ color: row.topswe.color, fontWeight: 700, fontSize: '1rem' }}>
                        ✅ {row.topswe.text}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      textAlign: 'center',
                      borderRight: '2px solid rgba(81, 104, 255, 0.1)'
                    }}
                  >
                    <Box sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      background: row.leetcode.bg,
                      border: `1px solid ${row.leetcode.color}50`
                    }}>
                      <Typography sx={{ color: row.leetcode.color, fontWeight: 600, fontSize: '0.95rem' }}>
                        {row.leetcode.color === '#c24150' ? '❌' : '⚠️'} {row.leetcode.text}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Box sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      background: row.neetcode.bg,
                      border: `1px solid ${row.neetcode.color}50`
                    }}>
                      <Typography sx={{ color: row.neetcode.color, fontWeight: 600, fontSize: '0.95rem' }}>
                        {row.neetcode.color === '#c24150' ? '❌' : row.neetcode.color === '#b7791f' ? '⚠️' : '✅'} {row.neetcode.text}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Bottom summary */}
        <Box sx={{ 
          mt: 6, 
          textAlign: 'center',
          p: 4,
          background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.1) 0%, rgba(142, 160, 255, 0.1) 100%)',
          borderRadius: 4,
          border: '2px solid rgba(81, 104, 255, 0.2)'
        }}>
          <Typography variant="h5" sx={{ 
            color: '#5168ff', 
            mb: 2, 
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2
          }}>
            <Typography sx={{ fontSize: '2rem' }}>🏆</Typography>
            TopSWE: The Clear Winner
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, maxWidth: 600, mx: 'auto' }}>
            Best value, fastest results, and taught by the world's #1 ranked LeetCode problem solver.
          </Typography>
          <Box sx={{ 
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
            px: 4,
            py: 2,
            background: 'linear-gradient(135deg, #15905f, #8bc34a)',
            borderRadius: 4,
            border: '2px solid rgba(76, 175, 80, 0.5)'
          }}>
            <Typography sx={{ color: 'text.primary', fontWeight: 700, fontSize: '1.1rem' }}>
              💎 Premium quality at unbeatable price
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Satisfaction Guarantee Section */}
      <Box sx={{ 
        my: 8,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        gap: 6,
        alignItems: 'center'
      }}>
        {/* Guarantee Section */}
        <Box sx={{
          p: 6,
          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(139, 195, 74, 0.1) 100%)',
          borderRadius: 6,
          border: '2px solid rgba(76, 175, 80, 0.3)',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #15905f, #8bc34a, #15905f)',
          }
        }}>
          <Box sx={{ 
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #15905f, #8bc34a)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 4,
            animation: 'shield-pulse 3s infinite',
            '@keyframes shield-pulse': {
              '0%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.7)' },
              '50%': { transform: 'scale(1.1)', boxShadow: '0 0 0 20px rgba(76, 175, 80, 0)' },
              '100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)' }
            }
          }}>
            <Typography variant="h3" sx={{ color: 'text.primary' }}>🛡️</Typography>
          </Box>
          
          <Typography 
            variant="h3" 
            sx={{ 
              mb: 4,
              background: 'linear-gradient(45deg, #15905f, #8bc34a)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800
            }}
          >
            100% Satisfaction Guarantee
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.6 }}>
            If you're not completely satisfied with the course, send me an email within 30 days.
          </Typography>
          
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mb: 4
          }}>
            {[
              "✅ No questions asked",
              "✅ Full refund available",
              "✅ Many satisfied students",
              // "✅ Keep all downloaded materials",
              "✅ 30-day guarantee period"
            ].map((item, index) => (
              <Box key={index} sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                p: 2,
                background: 'rgba(76, 175, 80, 0.1)',
                borderRadius: 3,
                border: '1px solid rgba(76, 175, 80, 0.2)'
              }}>
                <Typography sx={{ color: '#15905f', fontSize: '1.1rem', fontWeight: 600 }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
          
          <Typography variant="body1" sx={{ 
            color: '#15905f', 
            fontWeight: 600,
            fontStyle: 'italic',
            p: 3,
            background: 'rgba(76, 175, 80, 0.15)',
            borderRadius: 3,
            border: '1px solid rgba(76, 175, 80, 0.3)'
          }}>
            "We're confident in the value we provide, and we want you to feel confident in your investment."
          </Typography>
        </Box>

        {/* ROI Section */}
        <Box sx={{
          p: 6,
          background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.1) 0%, rgba(247, 147, 30, 0.1) 100%)',
          borderRadius: 6,
          border: '2px solid rgba(255, 107, 53, 0.3)',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #c24150, #b7791f, #c24150)',
          }
        }}>
          <Box sx={{ 
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #c24150, #b7791f)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 4,
            animation: 'money-spin 4s linear infinite',
            '@keyframes money-spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            }
          }}>
            <Typography variant="h3" sx={{ color: 'text.primary' }}>💰</Typography>
          </Box>
          
          <Typography 
            variant="h3" 
            sx={{ 
              mb: 4,
              background: 'linear-gradient(45deg, #c24150, #b7791f)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800
            }}
          >
            Best Investment in Your Career
          </Typography>
          
          <Box sx={{ 
            mb: 4,
            p: 4,
            background: 'rgba(255, 107, 53, 0.15)',
            borderRadius: 4,
            border: '1px solid rgba(255, 107, 53, 0.3)'
          }}>
            <Typography variant="h4" sx={{ color: '#c24150', fontWeight: 800, mb: 2 }}>
              $249 → $500,000+
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
              One-time payment can help you land a high-paying job at a top tech company
            </Typography>
            <Typography variant="h5" sx={{ 
              color: '#15905f', 
              fontWeight: 700,
              background: 'rgba(76, 175, 80, 0.2)',
              p: 2,
              borderRadius: 3,
              border: '1px solid rgba(76, 175, 80, 0.3)'
            }}>
              🚀 2000x ROI Potential
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 2,
            mb: 4
          }}>
            <Box sx={{ 
              p: 3,
              background: 'rgba(255, 107, 53, 0.1)',
              borderRadius: 3,
              border: '1px solid rgba(255, 107, 53, 0.2)'
            }}>
              <Typography variant="h6" sx={{ color: '#c24150', fontWeight: 700, mb: 1 }}>
                FAANG Salaries
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                $400k - $800k/year
              </Typography>
            </Box>
            <Box sx={{ 
              p: 3,
              background: 'rgba(255, 107, 53, 0.1)',
              borderRadius: 3,
              border: '1px solid rgba(255, 107, 53, 0.2)'
            }}>
              <Typography variant="h6" sx={{ color: '#c24150', fontWeight: 700, mb: 1 }}>
                Career Growth
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                Exponential income
              </Typography>
            </Box>
          </Box>
          
          <Typography variant="body1" sx={{ 
            color: '#b7791f', 
            fontWeight: 700,
            fontStyle: 'italic',
            p: 3,
            background: 'rgba(247, 147, 30, 0.15)',
            borderRadius: 3,
            border: '1px solid rgba(247, 147, 30, 0.3)',
            fontSize: '1.1rem'
          }}>
            "This is the best deal you'll ever make in your career"
          </Typography>
        </Box>
      </Box>

      {/* What You Get Section */}
      <Box sx={{ 
        my: 8,
        py: 8,
        px: 4,
        background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.08) 0%, rgba(142, 160, 255, 0.08) 100%)',
        borderRadius: 6,
        border: '2px solid rgba(81, 104, 255, 0.2)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #5168ff, #8ea0ff, #5168ff)',
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 6 }}>
          <Box sx={{ 
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #5168ff, #8ea0ff)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 3,
            animation: 'gift-bounce 2s infinite',
            '@keyframes gift-bounce': {
              '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0)' },
              '40%': { transform: 'translateY(-10px)' },
              '60%': { transform: 'translateY(-5px)' }
            }
          }}>
            <Typography variant="h4" sx={{ color: 'text.primary' }}>🎁</Typography>
          </Box>
          <Typography 
            variant="h3" 
            sx={{ 
              background: '#0a0d14',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800,
              textAlign: 'center'
            }}
          >
            What You Get
          </Typography>
        </Box>

        <Typography 
          variant="h6" 
          sx={{ 
            textAlign: 'center',
            mb: 6,
            color: 'text.secondary',
            maxWidth: 700,
            mx: 'auto',
            fontWeight: 400
          }}
        >
          Everything you need to master coding interviews and land your dream job
        </Typography>

        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 4,
          maxWidth: 1200,
          mx: 'auto'
        }}>
          {[
            {
              icon: "🎯",
              title: "Exclusive Pattern Templates",
              description: "Never seen before custom pattern Templates I created after solving 3500+ LeetCode problems",
              highlight: "Unique & Proven",
              color: "#5168ff"
            },
            {
              icon: "📚",
              title: "In-Depth Solutions",
              description: "In-depth solutions and articles written by TopSWE",
              highlight: "Expert Content",
              color: "#8ea0ff"
            },
            {
              icon: "🌐",
              title: "Complete Platform Access",
              description: "Access to everything on this website currently and in the future",
              highlight: "Lifetime Access",
              color: "#15905f"
            },
            {
              icon: "🗺️",
              title: "Structured Learning Path",
              description: "Structured learning path designed for maximum efficiency",
              highlight: "Optimized Route",
              color: "#c24150"
            },
            {
              icon: "🛡️",
              title: "Satisfaction Guarantee",
              description: "30-day money-back guarantee - we're confident you'll love it",
              highlight: "Risk-Free",
              color: "#b7791f"
            }
          ].map((item, index) => (
            <Box 
              key={index}
              sx={{ 
                p: 4,
                background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.1) 0%, rgba(142, 160, 255, 0.1) 100%)',
                borderRadius: 4,
                border: '2px solid rgba(81, 104, 255, 0.2)',
                position: 'relative',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(81, 104, 255, 0.2)',
                  border: '2px solid rgba(81, 104, 255, 0.4)',
                },
                ...(index === 4 && {
                  gridColumn: { lg: '1 / -1' },
                  maxWidth: { lg: '600px' },
                  mx: { lg: 'auto' }
                })
              }}
            >
              {/* Highlight badge */}
              <Box sx={{ 
                position: 'absolute',
                top: -2,
                right: -2,
                px: 2,
                py: 0.5,
                background: `linear-gradient(135deg, ${item.color}, ${item.color}dd)`,
                borderRadius: '0 4px 0 8px',
                border: `1px solid ${item.color}`,
                boxShadow: `0 4px 12px ${item.color}30`
              }}>
                <Typography variant="caption" sx={{ 
                  color: 'text.primary', 
                  fontWeight: 800,
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {item.highlight}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                {/* Icon circle */}
                <Box sx={{ 
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${item.color}, ${item.color}dd)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: `0 8px 20px ${item.color}30`,
                  animation: `feature-glow-${index} 3s ease-in-out infinite alternate`,
                  [`@keyframes feature-glow-${index}`]: {
                    '0%': { boxShadow: `0 8px 20px ${item.color}30` },
                    '100%': { boxShadow: `0 12px 30px ${item.color}50` }
                  }
                }}>
                  <Typography variant="h5" sx={{ color: 'text.primary' }}>{item.icon}</Typography>
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, pt: 0.5 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: item.color,
                      fontWeight: 800,
                      mb: 2,
                      fontSize: '1.2rem'
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'text.secondary',
                      lineHeight: 1.6,
                      fontSize: '1rem'
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Box>

              {/* Progress bar animation */}
              <Box sx={{ 
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: 'rgba(81, 104, 255, 0.1)',
                overflow: 'hidden'
              }}>
                <Box sx={{ 
                  width: '100%',
                  height: '100%',
                  background: `linear-gradient(90deg, ${item.color}, ${item.color}dd)`,
                  transform: 'translateX(-100%)',
                  animation: `progress-${index} 4s ease-in-out infinite`,
                  [`@keyframes progress-${index}`]: {
                    '0%': { transform: 'translateX(-100%)' },
                    '50%': { transform: 'translateX(0%)' },
                    '100%': { transform: 'translateX(100%)' }
                  }
                }} />
              </Box>
            </Box>
          ))}
        </Box>

        {/* Bottom CTA */}
        <Box sx={{ 
          mt: 8, 
          textAlign: 'center',
          p: 4,
          background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.1) 0%, rgba(142, 160, 255, 0.1) 100%)',
          borderRadius: 4,
          border: '2px solid rgba(81, 104, 255, 0.2)'
        }}>
          <Typography variant="h5" sx={{ 
            color: '#5168ff', 
            mb: 2, 
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2
          }}>
            <Typography sx={{ fontSize: '1.5rem' }}>💎</Typography>
            Everything for One Low Price
            <Typography sx={{ fontSize: '1.5rem' }}>💎</Typography>
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, maxWidth: 500, mx: 'auto' }}>
            Get immediate access to all these premium features and future updates for life.
          </Typography>
          <Typography variant="h6" sx={{ 
            color: '#15905f', 
            fontWeight: 800,
            background: 'rgba(76, 175, 80, 0.15)',
            p: 2,
            borderRadius: 3,
            border: '1px solid rgba(76, 175, 80, 0.3)',
            display: 'inline-block'
          }}>
            🚀 Total Value: $2000+ | Your Price: Just $249
          </Typography>
        </Box>
      </Box>

      {/* Success Stories Section */}
      <SuccessStories showTrustBadges={false} />

        {/* Join Community Section */}
        <Box sx={{ 
          my: 8,
          py: 8,
          px: 4,
          background: 'linear-gradient(135deg, rgba(114, 137, 218, 0.1) 0%, rgba(81, 104, 255, 0.1) 100%)',
          borderRadius: 6,
          border: '2px solid rgba(114, 137, 218, 0.3)',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #7289da, #5168ff, #7289da)',
          }
        }}>
          {/* Floating Discord icons for visual appeal */}
          <Box sx={{ 
            position: 'absolute',
            top: 30,
            left: 30,
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #7289da, #5168ff)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.6,
            animation: 'discord-float-1 5s ease-in-out infinite',
            '@keyframes discord-float-1': {
              '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
              '50%': { transform: 'translateY(-15px) rotate(180deg)' }
            }
          }}>
            <Typography sx={{ fontSize: '1.2rem' }}>💬</Typography>
          </Box>
          <Box sx={{ 
            position: 'absolute',
            top: 50,
            right: 40,
            width: 30,
            height: 30,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #5168ff, #7289da)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.7,
            animation: 'discord-float-2 4s ease-in-out infinite',
            '@keyframes discord-float-2': {
              '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
              '50%': { transform: 'translateY(-12px) rotate(-180deg)' }
            }
          }}>
            <Typography sx={{ fontSize: '1rem' }}>👥</Typography>
          </Box>

          <Box sx={{ position: 'relative', zIndex: 2 }}>
            {/* Header with animated Discord icon */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
              <Box sx={{ 
                width: 70,
                height: 70,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #7289da, #5168ff)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 3,
                animation: 'discord-glow 3s ease-in-out infinite alternate',
                '@keyframes discord-glow': {
                  '0%': { boxShadow: '0 0 20px rgba(114, 137, 218, 0.5)' },
                  '100%': { boxShadow: '0 0 30px rgba(114, 137, 218, 0.8)' }
                }
              }}>
                <Typography variant="h3" sx={{ color: 'text.primary' }}>💬</Typography>
              </Box>
              <Typography 
                variant="h3" 
                sx={{ 
                  background: 'linear-gradient(45deg, #7289da, #5168ff)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 800,
                  textAlign: 'center'
                }}
              >
                Join Our Community
              </Typography>
            </Box>

            {/* Main content with stats */}
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 4,
              mb: 6,
              maxWidth: 800,
              mx: 'auto'
            }}>
              {/* Left side - Community stats */}
              <Box sx={{ 
                p: 4,
                background: 'linear-gradient(135deg, rgba(114, 137, 218, 0.15) 0%, rgba(81, 104, 255, 0.15) 100%)',
                borderRadius: 4,
                border: '2px solid rgba(114, 137, 218, 0.3)',
                textAlign: 'center'
              }}>
                <Typography variant="h4" sx={{ 
                  color: '#7289da', 
                  fontWeight: 800, 
                  mb: 2,
                  animation: 'counter-grow 2s ease-out',
                  '@keyframes counter-grow': {
                    '0%': { transform: 'scale(0)' },
                    '100%': { transform: 'scale(1)' }
                  }
                }}>
                  A growing community 
                </Typography>
                <Typography variant="h6" sx={{ color: '#5168ff', fontWeight: 700, mb: 1 }}>
                  Active Members
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                  Engineers who've joined our thriving community
                </Typography>
              </Box>

              {/* Right side - Discord features */}
              <Box sx={{ 
                p: 4,
                background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.15) 0%, rgba(114, 137, 218, 0.15) 100%)',
                borderRadius: 4,
                border: '2px solid rgba(81, 104, 255, 0.3)',
                textAlign: 'center'
              }}>
                <Typography variant="h4" sx={{ 
                  color: '#5168ff', 
                  fontWeight: 800, 
                  mb: 2,
                  animation: 'feature-bounce 2s ease-out',
                  '@keyframes feature-bounce': {
                    '0%': { transform: 'translateY(-20px)', opacity: 0 },
                    '100%': { transform: 'translateY(0)', opacity: 1 }
                  }
                }}>
                  24/7
                </Typography>
                <Typography variant="h6" sx={{ color: '#7289da', fontWeight: 700, mb: 1 }}>
                  Support & Networking
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                  Get help anytime from peers and experts
                </Typography>
              </Box>
            </Box>

            {/* Call to action */}
            <Typography 
              variant="h4" 
              sx={{ 
                mb: 3,
                color: 'text.secondary',
                fontWeight: 600,
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.4
              }}
            >
              Be part of our growing community of <Box component="span" sx={{ 
                color: '#5168ff', 
                fontWeight: 800,
                textShadow: '0 0 10px rgba(81, 104, 255, 0.5)'
              }}>ambitious engineers</Box>
            </Typography>

            {/* Discord join button */}
            <Box sx={{ 
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              px: 6,
              py: 3,
              mb: 4,
              background: 'linear-gradient(135deg, #7289da, #5168ff)',
              borderRadius: 5,
              border: '2px solid rgba(114, 137, 218, 0.5)',
              cursor: 'pointer',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 8px 24px rgba(114, 137, 218, 0.3)',
              '&:hover': { 
                transform: 'translateY(-4px) scale(1.05)',
                boxShadow: '0 16px 32px rgba(114, 137, 218, 0.5)',
                background: 'linear-gradient(135deg, #5168ff, #7289da)',
              }
            }}>
              <Typography sx={{ fontSize: '1.8rem' }}>💬</Typography>
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 800 }}>
                Join Premium Discord Community
              </Typography>
              <Typography sx={{ fontSize: '1.8rem' }}>🚀</Typography>
            </Box>

            {/* Features list */}
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
              gap: 3,
              maxWidth: 700,
              mx: 'auto'
            }}>
              {[
                { icon: "🤝", text: "Network with Peers" },
                { icon: "💡", text: "Get Expert Advice" },
                { icon: "🎯", text: "Interview Practice" }
              ].map((item, index) => (
                <Box key={index} sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  p: 3,
                  background: 'rgba(114, 137, 218, 0.1)',
                  borderRadius: 3,
                  border: '1px solid rgba(114, 137, 218, 0.2)',
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}>
                  <Typography sx={{ fontSize: '2rem' }}>{item.icon}</Typography>
                  <Typography sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600, 
                    fontSize: '0.9rem',
                    textAlign: 'center'
                  }}>
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* FAQ Section */}
        <Box sx={{ 
          my: 8,
          py: 8,
          px: 4,
          background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.08) 0%, rgba(142, 160, 255, 0.08) 100%)',
          borderRadius: 6,
          border: '2px solid rgba(81, 104, 255, 0.2)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #5168ff, #8ea0ff, #5168ff)',
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 6 }}>
            <Box sx={{ 
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #5168ff, #8ea0ff)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 3,
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(81, 104, 255, 0.7)' },
                '70%': { transform: 'scale(1.05)', boxShadow: '0 0 0 10px rgba(81, 104, 255, 0)' },
                '100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(81, 104, 255, 0)' }
              }
            }}>
              <Typography variant="h4" sx={{ color: 'text.primary' }}>❓</Typography>
            </Box>
            <Typography 
              variant="h3" 
              sx={{ 
                background: '#0a0d14',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 800,
                textAlign: 'center'
              }}
            >
              Frequently Asked Questions
            </Typography>
          </Box>

          <Typography 
            variant="h6" 
            sx={{ 
              textAlign: 'center',
              mb: 6,
              color: 'text.secondary',
              maxWidth: 600,
              mx: 'auto',
              fontWeight: 400
            }}
          >
            Got questions? We've got answers. Here's what our students ask most.
          </Typography>

          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            {faqItems.map((item, index) => (
              <Accordion 
                key={index}
                sx={{ 
                  mb: 3,
                  borderRadius: '16px !important',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.1) 0%, rgba(142, 160, 255, 0.1) 100%)',
                  border: '2px solid rgba(81, 104, 255, 0.2)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:before': {
                    display: 'none',
                  },
                  '&:hover': {
                    transform: 'translateY(-6px) scale(1.02)',
                    boxShadow: '0 20px 40px rgba(81, 104, 255, 0.2)',
                    border: '2px solid rgba(81, 104, 255, 0.4)',
                  },
                  '&.Mui-expanded': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 16px 32px rgba(81, 104, 255, 0.15)',
                    border: '2px solid rgba(81, 104, 255, 0.3)',
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <Box sx={{ 
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #5168ff, #8ea0ff)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'transform 0.3s ease',
                      '&:hover': { transform: 'rotate(180deg)' }
                    }}>
                      <ExpandMoreIcon sx={{ color: 'text.primary', fontSize: 24 }} />
                    </Box>
                  }
                  sx={{
                    py: 2,
                    px: 3,
                    background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.05) 0%, rgba(142, 160, 255, 0.05) 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.1) 0%, rgba(142, 160, 255, 0.1) 100%)',
                    },
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      my: 2
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: '#0a0d14',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      color: 'text.primary'
                    }}>
                      {index + 1}
                    </Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        background: '#0a0d14',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 700,
                        fontSize: '1.1rem'
                      }}
                    >
                      {item.question}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    px: 4,
                    py: 3,
                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.3) 0%, rgba(26, 26, 26, 0.3) 100%)',
                    borderTop: '1px solid rgba(81, 104, 255, 0.1)'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ 
                      width: 4,
                      minHeight: 40,
                      background: 'linear-gradient(180deg, #5168ff, #8ea0ff)',
                      borderRadius: 2,
                      mt: 0.5
                    }} />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.7,
                        fontSize: '1rem',
                        flex: 1
                      }}
                    >
                      {item.answer}
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* Bottom CTA in FAQ */}
          <Box sx={{ 
            mt: 6, 
            textAlign: 'center',
            p: 4,
            background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.1) 0%, rgba(142, 160, 255, 0.1) 100%)',
            borderRadius: 4,
            border: '1px solid rgba(81, 104, 255, 0.2)'
          }}>
            <Typography variant="h6" sx={{ color: '#5168ff', mb: 2, fontWeight: 600 }}>
              Still have questions?
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
              Join our community and get instant answers from other students and instructors.
            </Typography>
            <Box sx={{ 
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 3,
              py: 1.5,
              background: 'linear-gradient(135deg, #5168ff, #8ea0ff)',
              borderRadius: 3,
              cursor: 'pointer',
              transition: 'transform 0.3s ease',
              '&:hover': { transform: 'translateY(-2px)' }
            }}>
              <Typography sx={{ color: 'text.primary', fontWeight: 600 }}>💬</Typography>
              <Typography sx={{ color: 'text.primary', fontWeight: 600 }}>Ask in Discord</Typography>
            </Box>
          </Box>
        </Box>

      {/* Final CTA Section */}
      <Box sx={{ 
        my: 10,
        py: 12,
        px: 4,
        background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.1) 0%, rgba(142, 160, 255, 0.1) 100%)',
        borderRadius: 8,
        border: '3px solid rgba(81, 104, 255, 0.3)',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '6px',
          background: 'linear-gradient(90deg, #5168ff, #8ea0ff, #c24150, #15905f, #5168ff)',
          animation: 'rainbow-slide 3s linear infinite',
          '@keyframes rainbow-slide': {
            '0%': { backgroundPosition: '0% 50%' },
            '100%': { backgroundPosition: '200% 50%' }
          }
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 20%, rgba(81, 104, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(142, 160, 255, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        }
      }}>
        {/* Floating elements for visual appeal */}
        <Box sx={{ 
          position: 'absolute',
          top: 20,
          left: 20,
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #5168ff, #8ea0ff)',
          opacity: 0.6,
          animation: 'float-left 6s ease-in-out infinite',
          '@keyframes float-left': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-20px) rotate(180deg)' }
          }
        }} />
        <Box sx={{ 
          position: 'absolute',
          top: 40,
          right: 30,
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #c24150, #b7791f)',
          opacity: 0.7,
          animation: 'float-right 4s ease-in-out infinite',
          '@keyframes float-right': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-15px) rotate(-180deg)' }
          }
        }} />

        <Box sx={{ position: 'relative', zIndex: 2 }}>
          {/* Urgency badge */}
          <Box sx={{ 
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 4,
            py: 1.5,
            mb: 4,
            background: 'linear-gradient(135deg, #c24150, #b7791f)',
            borderRadius: 5,
            border: '2px solid rgba(255, 107, 53, 0.5)',
            animation: 'urgent-glow 2s ease-in-out infinite alternate',
            '@keyframes urgent-glow': {
              '0%': { boxShadow: '0 0 20px rgba(255, 107, 53, 0.5)' },
              '100%': { boxShadow: '0 0 30px rgba(255, 107, 53, 0.8)' }
            }
          }}>
            <Typography sx={{ fontSize: '1.5rem' }}>🔥</Typography>
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 800 }}>
              LIMITED TIME: Launch Pricing
            </Typography>
            <Typography sx={{ fontSize: '1.5rem' }}>🔥</Typography>
          </Box>

          {/* Main headline with dramatic effect */}
          <Typography 
            variant="h2" 
            sx={{ 
              mb: 4,
              background: 'linear-gradient(45deg, #5168ff, #8ea0ff, #c24150)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 900,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              lineHeight: 1.2,
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              animation: 'title-pulse 3s ease-in-out infinite',
              '@keyframes title-pulse': {
                '0%, 100%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.02)' }
              }
            }}
          >
            What Are You Waiting For?
          </Typography>

          {/* Compelling sub-headline */}
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 6,
              color: 'text.secondary',
              fontWeight: 600,
              maxWidth: 800,
              mx: 'auto',
              lineHeight: 1.4
            }}
          >
            You're <Box component="span" sx={{ 
              color: '#5168ff', 
              fontWeight: 800,
              textShadow: '0 0 10px rgba(81, 104, 255, 0.5)'
            }}>one click away</Box> from your dream $500k+ FAANG job
          </Typography>

          {/* Value proposition cards */}
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
            gap: 3,
            mb: 8,
            maxWidth: 900,
            mx: 'auto'
          }}>
            {[
              { icon: "⚡", title: "Instant Access", desc: "Start learning immediately" },
              { icon: "🎯", title: "Proven Results", desc: "90% job offer success rate" },
              { icon: "💎", title: "Lifetime Value", desc: "One payment, forever access" }
            ].map((item, index) => (
              <Box key={index} sx={{ 
                p: 4,
                background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.1) 0%, rgba(142, 160, 255, 0.1) 100%)',
                borderRadius: 4,
                border: '2px solid rgba(81, 104, 255, 0.2)',
                textAlign: 'center',
                transition: 'transform 0.3s ease',
                '&:hover': { 
                  transform: 'translateY(-8px)',
                  boxShadow: '0 16px 32px rgba(81, 104, 255, 0.2)'
                }
              }}>
                <Typography sx={{ fontSize: '3rem', mb: 2 }}>{item.icon}</Typography>
                <Typography variant="h6" sx={{ color: '#5168ff', fontWeight: 700, mb: 1 }}>
                  {item.title}
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                  {item.desc}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Main CTA Button with enhanced design */}
          <Box sx={{ mb: 6 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => handleSubscription(process.env.REACT_APP_STRIPE_PRICE_LIFETIME, true)}
              disabled={hasLifetimeAccess}
              sx={{
                py: 3,
                px: 8,
                fontSize: '1.4rem',
                fontWeight: 900,
                background: 'linear-gradient(135deg, #5168ff, #8ea0ff)',
                borderRadius: '50px',
                border: '3px solid rgba(81, 104, 255, 0.5)',
                boxShadow: '0 15px 35px rgba(81, 104, 255, 0.4)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                textTransform: 'none',
                minWidth: 320,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  transition: 'left 0.6s',
                },
                '&:hover': {
                  transform: 'translateY(-6px) scale(1.05)',
                  boxShadow: '0 25px 50px rgba(81, 104, 255, 0.6)',
                  background: 'linear-gradient(135deg, #8ea0ff, #5168ff)',
                  '&::before': {
                    left: '100%',
                  }
                },
                '&:disabled': {
                  background: 'linear-gradient(135deg, #15905f, #8bc34a)',
                  color: 'text.primary',
                  '&:hover': {
                    transform: 'none',
                    boxShadow: '0 15px 35px rgba(76, 175, 80, 0.4)',
                  }
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
                <Typography sx={{ fontSize: '1.8rem' }}>🚀</Typography>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {user ? (
                    hasLifetimeAccess ? '✅ You Have Lifetime Access!' : 
                    'Get Lifetime Access Now - $249'
                  ) : (
                    'Sign In to Get Started'
                  )}
                </Typography>
                <Typography sx={{ fontSize: '1.8rem' }}>🚀</Typography>
              </Box>
            </Button>

            {/* Urgency text */}
            {!hasLifetimeAccess && (
              <Typography 
                variant="body1" 
                sx={{ 
                  mt: 3,
                  color: '#c24150',
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  animation: 'urgency-blink 2s ease-in-out infinite',
                  '@keyframes urgency-blink': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.7 }
                  }
                }}
              >
                ⏰ Launch price ends soon - Save $200 today!
              </Typography>
            )}
          </Box>

          {/* Trust indicators */}
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 4,
            flexWrap: 'wrap',
            mb: 6
          }}>
            {[
              { icon: "🛡️", text: "30-Day Guarantee" },
              { icon: "⚡", text: "Instant Access" },
              { icon: "💳", text: "Secure Payment" },
              { icon: "👥", text: "Join 1000+ Engineers" }
            ].map((item, index) => (
              <Box key={index} sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 3,
                py: 1.5,
                background: 'rgba(81, 104, 255, 0.1)',
                borderRadius: 3,
                border: '1px solid rgba(81, 104, 255, 0.2)'
              }}>
                <Typography sx={{ fontSize: '1.2rem' }}>{item.icon}</Typography>
                <Typography sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.9rem' }}>
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Final motivational message */}
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'text.secondary',
              fontStyle: 'italic',
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 4
            }}
          >
            "The best time to plant a tree was 20 years ago. The second best time is now."
          </Typography>

          <Typography 
            variant="body1" 
            sx={{ 
              color: '#5168ff',
              fontWeight: 600,
              fontSize: '1.1rem'
            }}
          >
            Join thousands of successful engineers who transformed their careers with TopSWE 🎯
          </Typography>
        </Box>
      </Box>
      </Box>
      </Container>
    </Box>
  );
}

export default Purchase; 
